import { html, nothing } from "lit";
import { customElement, state } from 'lit/decorators.js';
import store from "../../../store/store";
import { connect } from 'pwa-helpers';
import { StepId } from "../shop-checkout.interface";
import { ShopCart } from "../../cart/shop-cart/interfaces/shop-cart.interface";
import { CheckoutStepsBase } from "../checkout-steps-base";
import "./shipping-method-k12-storefront-checkout-step";
import "./payment-billing-k12-storefront-checkout-step";
import "./../shipping-address-checkout-step";
import { StoreState } from "../../../store/store.interface";
import { MessageType } from "../../alert/global-message.interface";

/**
 * An element to load k12 storefront checkout steps and complete the checkout process.
 */
@customElement('mhecomm-k12-checkout-steps')
export class K12CheckoutSteps extends connect(store)(CheckoutStepsBase) {

  @state()
  private showCustomPriceMessage = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mhecomm-custom-price', this.onCustomPriceEventHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('mhecomm-custom-price', this.onCustomPriceEventHandler);
  }

  stateChanged(state: StoreState) {
    this.updateCheckoutStepsState(state);
    this.handleApiFailures(state);
  }

  override setStepOrderStepIdMap(){
    if (this.shippable) {
      this.setK12StepOrder();
    } else {
      this.setDigitalOnlySetOrder();
    }
    this.isLoading = false;
  }

  setK12StepOrder() {
    this.stepOrderStepIdMap['1'] = StepId.ShippingAddressStep;
    this.stepOrderStepIdMap['2'] = StepId.PaymentBillingAddressStep;
    this.stepOrderStepIdMap['3'] = StepId.ShippingMethodStep;
  }

  setDigitalOnlySetOrder() {
    this.stepOrderStepIdMap['1'] = StepId.PaymentBillingAddressStep;
  }

  override setCartCheckoutContext(cart: ShopCart){
    this.shippable = cart?.shippable || false;
  }

  override renderCheckoutSteps() {
    let checkoutSteps = this.renderDigitalOnlyCheckoutSteps();
    if (this.shippable) {
      checkoutSteps = this.renderK12AdminCheckoutSteps();
    }
    return html`${this.customPriceMessage()}${checkoutSteps}`;
  }

  customPriceMessage(){
    return html`${ this.showCustomPriceMessage ? 
      html`<mhecomm-alert message="Discounts have been applied." type="${MessageType.success}" ?dismissible=${true}></mhecomm-alert>`
      : nothing }`;
  }

  renderK12AdminCheckoutSteps(){
    return html`
      <mhecomm-shipping-address-checkout-step
        ?forceComplete = "${this.forceComplete}"
        ?showSequenceOrder = "${true}"
        order = 1
        activeStepValue = "${this.activeStepValue}"
        id=${StepId.ShippingAddressStep}
        stepName="Shipping Address"
        nextStepButtonName="Continue to Payment and Billing"
        ?showSchoolFreightCostMessage = "${true}"
        ?skipFreightCalculation = "${true}">
      </mhecomm-shipping-address-checkout-step>
      <mhecomm-payment-billing-k12-storefront-checkout-step
        ?forceComplete = "${this.forceComplete}"
        ?showSequenceOrder = "${true}"
        order = 2
        activeStepValue = "${this.activeStepValue}"
        id=${StepId.PaymentBillingAddressStep}
        stepName="Payment and Billing"
        nextStepButtonName="Continue to Shipping Method">
      </mhecomm-payment-billing-k12-storefront-checkout-step >
      <mhecomm-shipping-method-k12-storefront-checkout-step
        ?forceComplete = "${this.forceComplete}"
        ?showSequenceOrder = "${true}"
        order = 3
        activeStepValue = "${this.activeStepValue}"
        id=${StepId.ShippingMethodStep}
        stepName="Shipping Method"
        nextStepButtonName="Review Order">
      </mhecomm-shipping-method-k12-storefront-checkout-step>
    `;
  }

  renderDigitalOnlyCheckoutSteps(){
    return html`
      <mhecomm-payment-billing-k12-storefront-checkout-step 
        ?forceComplete="${this.forceComplete}"
        id=${StepId.PaymentBillingAddressStep}
        stepName="Payment and Billing"
        nextStepButtonName="Review Order">
      </mhecomm-payment-billing-k12-storefront-checkout-step>
    `;
  }

  /**
   * When all steps are completed update checkout steps state.
   */
  protected completeSteps() {
    this.showCustomPriceMessage = false;
    super.completeSteps();
  }

  /**
   * Triggered by payment billing checkout step component when custom prices are loaded.
   */
   onCustomPriceEventHandler() {
    this.showCustomPriceMessage = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-k12-checkout-steps': K12CheckoutSteps;
  }
}
